import React, { useState } from 'react';
import { Grid, Button } from '@material-ui/core';
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Alert from "@material-ui/lab/Alert";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemText from "@material-ui/core/ListItemText";
import Box from "@material-ui/core/Box";
import { useDispatch, useSelector } from 'react-redux';
import { getGPIBankData, setGPIBank, setGPIBankError, savePaymentToken } from '../../../../Store/purchase.slice';
import featureFlags from '../../../../featureFlags';
import './GPIBankAccountForm.scss';

const GPIBankAccountForm = () => {
    const dispatch = useDispatch();
    const gpiBankData = useSelector(getGPIBankData);
    const [editToken, setEditToken] = useState(gpiBankData.accounts.length < 1);
    const [accountType, setAccountType] = useState('');
    const [routingNumber, setRoutingNumber] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [businessName, setBusinessName] = useState('');
    const [isBusinessAccount, setIsBusinessAccount] = useState(false);

    const resetFields = () => {
        setAccountType('');
        setAccountNumber('');
        setRoutingNumber('');
        setFirstName('');
        setLastName('');
        setBusinessName('');
    }

    const handleSave = async () => {
        const name = isBusinessAccount ?
        { business_name: businessName } :
        {
            first_name: firstName,
            last_name: lastName,
        };

        const account = {
            bank_account_number: accountNumber,
            bank_account_type: accountType,
            bank_routing_number: routingNumber,
            name,
        };

        try {
            await dispatch(savePaymentToken({
                gpi_bank: account
            })).unwrap();

            resetFields();
            setEditToken(false);
        } catch (errors) {
            const error = errors[0].message;
            dispatch(setGPIBankError(error));
        }
    }

    const handleCancel = () => {
        resetFields();
        setEditToken(false);
    }

    const nameIsMissing = isBusinessAccount ? businessName === '' : (firstName === '' || lastName === '');
    const disableSave = (routingNumber.length !== 9 || accountNumber.length < 10 || accountNumber.length > 12 ||
                    accountType === '' || nameIsMissing);

    const getButtonText = (isBusinessAccount) => {
        return isBusinessAccount ? 'individual' : 'business'
    }

    const renderNameFieldsAndButton = () => {
        const displayBusinessName = featureFlags.BUSINESS_NAME && isBusinessAccount;
        const displayButton = featureFlags.BUSINESS_NAME;
        const buttonText = `Is this a ${getButtonText(displayBusinessName)} account?`;

        const renderNameFields = () => displayBusinessName ?
            <Grid item xs={12} sm={12}>
                <TextField
                    label="Business Name"
                    name="business-name"
                    variant="outlined"
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={businessName}
                    onChange={(event) => setBusinessName(event.target.value)}
                    inputProps={{"data-cy": "bank-account-add-business-name-input"}}
                />
            </Grid>
            :
            <>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="First Name"
                        name="first-name"
                        variant="outlined"
                        required
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={firstName}
                        onChange={(event) => setFirstName(event.target.value)}
                        inputProps={{"data-cy": "bank-account-add-first-name-input"}}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Last Name"
                        name="last-name"
                        variant="outlined"
                        required
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={lastName}
                        onChange={(event) => setLastName(event.target.value)}
                        inputProps={{"data-cy": "bank-account-add-last-name-input"}}
                    />
                </Grid>
            </>
        ;

        return <>
            {renderNameFields()}
            {displayButton &&
                <button type="button"
                    className="parkchirp--button-as-link"
                    id="account-type-btn"
                    onClick={() => setIsBusinessAccount(!isBusinessAccount)}
                    data-cy={`bank-account-${getButtonText(displayBusinessName)}-toggle-button`}>
                    {buttonText}
                </button>
            }
        </>
    }

    return <Grid container spacing={3}>
        {!!editToken ?
            <>
                {renderNameFieldsAndButton()}
                <Grid item xs={12} sm={12}>
                    <TextField
                        label="Routing #"
                        name="routing-number"
                        variant="outlined"
                        required
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={routingNumber}
                        onChange={(event) => setRoutingNumber(event.target.value)}
                        inputProps={{"data-cy": "bank-account-add-routing-number-input"}}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Account Type"
                        name="account-type"
                        variant="outlined"
                        required
                        fullWidth
                        select
                        InputLabelProps={{ shrink: true }}
                        value={accountType}
                        onChange={(event) => setAccountType(event.target.value)}
                        data-cy="bank-account-add-account-type-text-field"
                    >
                        <MenuItem value={'CHECKING'} data-cy="account-type-checking-menu-item">Checking</MenuItem>
                        <MenuItem value={'SAVINGS'} data-cy="account-type-savings-menu-item">Savings</MenuItem>
                    </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Account #"
                        name="account-number"
                        variant="outlined"
                        required
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={accountNumber}
                        onChange={(event) => setAccountNumber(event.target.value)}
                        inputProps={{"data-cy": "bank-account-add-account-number-input"}}
                    />
                </Grid>
                {gpiBankData.error ? <Grid item xs={12}><Alert severity="error" data-cy="bank-account-add-alert">{gpiBankData.error}</Alert></Grid> : ''}
            </>
        :
            <Grid item xs={12}>
                <List>
                    {gpiBankData.accounts.map((a,i) => <ListItem key={a.token} selected={a.token === gpiBankData.default_source}
                                                        onClick={() => dispatch(setGPIBank(a.token))} button data-cy={`bank-account-option-${i}`}>
                        <ListItemText primary={`Account ending in ${a.last4}`}
                                    secondary={`${a.accountType}`}
                                    primaryTypographyProps={{"data-cy": "bank-account-option-account-number-text"}}
                                    secondaryTypographyProps={{"data-cy": "bank-account-option-account-type-text"}}/>
                    </ListItem>)}
                </List>
            </Grid>
        }
        <Grid item xs={12}>
            {!!editToken ? 
                <Box display="flex" justifyContent="space-between">
                    <Button variant="outlined" color="secondary"
                            onClick={ handleCancel } data-cy="bank-account-add-cancel-button">Cancel</Button>
                    <Button variant="contained" color="primary" disabled={disableSave}
                            onClick={ handleSave } data-cy="bank-account-add-submit-button">Save</Button>
                </Box>
                :
                <>
                    <div>
                        <Button variant="contained" color="primary"
                                onClick={() => setEditToken(true)} data-cy="bank-account-add-button">Add Bank Account</Button>
                    </div>
                    <div>
                        <Typography variant='caption'>
                            In order to verify this account, you may see a micro charge that will be refunded.</Typography>
                    </div>
                </>
            }
        </Grid>
    </Grid>
};

export default GPIBankAccountForm;